import { Graphics, Sprite } from 'pixi.js';
import type { BattleEndReason, Move, SkillId, Stance } from '@shared/protocol/messages';
import type { App } from '../../app/App';
import { Scene } from '../core/Scene';
import type { ViewportInfo } from '../core/Viewport';
import { FighterView } from '../entities/FighterView';
import { FloatingNumbers, ParticlePool } from '../entities/Effects';
import { acquireArt, nominalScale } from '../entities/art';
import { Ease } from '../animation/Ease';
import { motion } from '../animation/Motion';
import { MOVES } from '../combat/rps';
import { RULES, skillDef, tierDef } from '../combat/rules';
import { ultimateReady } from '../combat/engine';
import { heroArtId, heroCombat, heroDrawing } from '../systems/hero';
import { aiLevelOf, findMonster, hasBuiltInName, isBossMonster, monsterArt } from '../systems/monsters';
import { TWISTS, traceDamage, type PageEvent, type RoundPreview } from '../combat/twists';
import { traceGame } from '../../ui/traceGame';
import { towerFloorOf } from '../systems/tower';
import { LOSS_COIN_PERCENT, applyBattle, addMemory, applyVictoryRoll, forfeitUnfinished, markBattleStart, nextFight, type RewardSummary } from '../systems/progression';
import { PRESET_MONSTERS, heroClass, itemDef } from '../data/presets';
import type { Hero, Monster } from '../systems/model';
import { drawThumbnail } from '../../drawing/DrawingTextures';
import { artImg, button, confirmBox, meter, setMeter, thumb } from '../../ui/components';
import { h, setChildren } from '../../ui/dom';
import { deskProps, paperScenery } from '../../ui/notebookArt';
import { icon as sharedIcon } from '../../ui/icons';
import { t } from '../../i18n';
import { cosmeticDef } from '../data/cosmetics';
import { stampFace } from '../../ui/cosmetics';
import { PveDriver, PvpDriver, type BattleDriver, type BattleEnd, type LocalRound } from './battle/drivers';
import { reportCommunityFight } from './battle/community';
import { damageDealt } from '../systems/community';
import { BATTLE_DICE, rollDie, type DiceRollResult } from '../combat/dice';
import { reportClassBattle } from '../../networking/classroom';
import { INJURY_LOSSES, INJURY_MINUTES } from '../systems/injury';

export type BattleParams = { mode: 'pve'; monsterId: string } | { mode: 'pvp' };

// English source text only: translate with t() when rendering (the language is set after modules load).
const MOVE_LABEL: Record<Move | 'timeout', string> = { rock: 'Rock', paper: 'Paper', scissors: 'Scissors', timeout: 'nothing (time out)' };
const REASON_LABEL: Record<BattleEndReason, string> = { ko: 'knockout', rounds: 'out of rounds', forfeit: 'running away', timeout: 'time out', disconnect: 'disconnect' };
type PickStance = Exclude<Stance, 'none' | 'ultimate'>;
const STANCE_LABEL: Record<Exclude<Stance, 'none'>, string> = { guard: 'Guard', focus: 'Focus', ultimate: 'Ultimate' };
/** Each class's ultimate (English source text, see rules.json `ultimates`). */
const ULT_NAME: Record<SkillId, string> = {
  none: 'Big Doodle Blast',
  crayon_smash: 'Mega Crayon Crash',
  sharp_edge: 'Paper Cut Storm',
  paper_shield: 'Fortress Fold',
  eraser_mend: 'Clean Page',
  doodle_dodge: 'Vanishing Sketch',
  ink_combo: 'Ink Tsunami',
  lucky_star: 'Shooting Star Wish',
  highlighter_drain: 'Neon Drain',
  sharpener_spikes: 'Pencil Fortress',
  glue_guard: 'Glue Wall',
  marker_rage: 'Permanent Marker Fury',
};
const MOVE_EMOJI: Record<Move, string> = { rock: '✊', paper: '✋', scissors: '✌️' };
/** Page events (combat/twists.ts): emoji, name and what happens this round. */
const EVENT_INFO: Record<PageEvent, [string, string, string]> = {
  gold_star: ['⭐', 'Gold Star', 'The winner of this round heals {n}% HP.'],
  ink_rain: ['💧', 'Ink Rain', 'Both sides get +{n} ink after this round.'],
  double_page: ['📖', 'Double Page', "This round's hit lands twice!"],
  tie_sting: ['✂️', 'Paper Cut', 'A tie this round stings both sides.'],
  shuffle: ['🌀', 'Mixed-up Hands', 'The hand buttons got shuffled. Look before you tap!'],
};
const EVENT_NUMBER: Record<PageEvent, number> = { gold_star: TWISTS.goldStarHealPercent, ink_rain: TWISTS.inkRain, double_page: 0, tie_sting: TWISTS.tieStingPercent, shuffle: 0 };
/** What a class ultimate does, with the numbers from rules.json. */
function ultimateTip(skill: SkillId): string {
  const u = RULES.ultimates[skill] ?? RULES.ultimates.none;
  const parts = [
    u.damagePercent ? t("hits for {n}% of the foe's max HP", { n: u.damagePercent }) : '',
    u.rageBonusPercent ? t('+{n}% more below half HP', { n: u.rageBonusPercent }) : '',
    u.drain ? t('heals what it hits') : '',
    u.healPercent ? t('heals {n}% of your max HP', { n: u.healPercent }) : '',
    u.shield ? t("blocks the hand's hit this round") : '',
  ].filter(Boolean);
  return t('{name}: {effects}. Fill the ink gauge by winning rounds.', { name: t(ULT_NAME[skill] ?? ULT_NAME.none), effects: parts.join(', ') });
}
/** Stance explanation with the numbers from rules.json. */
function stanceTip(s: PickStance): string {
  const st = RULES.stances;
  return s === 'guard'
    ? t('Guard: take {n}% less damage if you lose (your hit is {n}% weaker if you win).', { n: st.guardPercent })
    : t('Focus: +{bonus}% damage if you win, but you take +{risk}% if you lose.', { bonus: st.focusBonusPercent, risk: st.focusVulnerablePercent });
}
const signed = (n: number): string => `${n >= 0 ? '+' : ''}${n}`;
const KEYS: Record<string, Move> = { '1': 'rock', '2': 'paper', '3': 'scissors', r: 'rock', p: 'paper', s: 'scissors' };
const STANCE_KEYS: Record<string, PickStance> = { g: 'guard', f: 'focus' };

/** Tiny crayon icons for the battle UI (inline SVG, 24×24). */
const ICONS: Record<string, string> = {
  sword: '<path d="M5 19 L16 8 M14 5 L19 5 L19 10" stroke-width="2.6"/><path d="M4 15 L9 20 M3 21 L6 18" stroke-width="2.4"/><path d="M16 8 L19 5" stroke="#8ec2f3" stroke-width="3"/>',
  shield: '<path d="M12 3 L20 6 C20 13 17 18 12 21 C7 18 4 13 4 6 Z" fill="#8ec2f3"/><path d="M12 6 V18" stroke-width="1.6"/>',
  clover: '<circle cx="9" cy="9" r="3.6" fill="#a9dc8a"/><circle cx="15" cy="9" r="3.6" fill="#a9dc8a"/><circle cx="12" cy="14" r="3.6" fill="#a9dc8a"/><path d="M12 15 C12 18 11 20 9 21"/>',
  heart: '<path d="M12 20 C3 14 3 7 8 6 C10 6 11.5 7.5 12 9 C12.5 7.5 14 6 16 6 C21 7 21 14 12 20 Z" fill="#e4572e"/>',
  swirl: '<path d="M12 12 A1.5 1.5 0 0 1 15 12 A3 3 0 0 1 9 12 A4.5 4.5 0 0 1 18 12 A6 6 0 0 1 6 12" stroke="#8e5bd6" stroke-width="2.2"/>',
  crown: '<path d="M4 18 L5 8 L9 12 L12 6 L15 12 L19 8 L20 18 Z" fill="#f6d55c"/>',
  skull: '<path d="M5 11 C5 6 8 4 12 4 C16 4 19 6 19 11 C19 14 17 15 16 16 V19 H8 V16 C7 15 5 14 5 11 Z" fill="#fff"/><circle cx="9.5" cy="11" r="1.8" fill="#3b3b3b"/><circle cx="14.5" cy="11" r="1.8" fill="#3b3b3b"/>',
  bulb: '<path d="M9 16 C6 14 6 9 8 7 C10 5 14 5 16 7 C18 9 18 14 15 16 Z" fill="#f6d55c"/><path d="M9.5 18.5 H14.5 M10.5 21 H13.5"/>',
  book: '<path d="M3 6 C6 5 9 5 12 7 C15 5 18 5 21 6 V19 C18 18 15 18 12 20 C9 18 6 18 3 19 Z" fill="#fff"/><path d="M12 7 V20"/>',
  star: '<path d="M12 3 L14.6 9 L21 9.4 L16 13.4 L17.6 20 L12 16.4 L6.4 20 L8 13.4 L3 9.4 L9.4 9 Z" fill="#f6d55c"/>',
  smile: '<circle cx="12" cy="12" r="8"/><path d="M8.5 14 C10 16.5 14 16.5 15.5 14"/><path d="M9.5 9.5 v1 M14.5 9.5 v1"/>',
  drop: '<path d="M12 3 C9 8 6 11 6 15 A6 6 0 0 0 18 15 C18 11 15 8 12 3 Z" fill="#7f8ee8"/><path d="M9.5 15 C9.5 16.6 10.5 17.6 12 17.8" stroke="#fff"/>',
  gift: '<rect x="4" y="9" width="16" height="11" fill="#ffd3df"/><path d="M4 13 H20 M12 9 V20 M12 9 C9 4 6 6 8 9 M12 9 C15 4 18 6 16 9"/>',
};

function icon(name: keyof typeof ICONS, cls = ''): SVGSVGElement {
  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  svg.setAttribute('viewBox', '0 0 24 24');
  svg.setAttribute('class', `bt-icon ${name} ${cls}`);
  svg.setAttribute('aria-hidden', 'true');
  svg.innerHTML = `<g fill="none" stroke="#3b3b3b" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">${ICONS[name] ?? ''}</g>`;
  return svg;
}

/** ★★☆ */
function starText(n: number): string {
  return '★'.repeat(n) + '☆'.repeat(Math.max(0, 3 - n));
}

interface Side {
  card: HTMLElement;
  portrait: HTMLCanvasElement;
  hpMeter: HTMLElement;
  hpText: HTMLElement;
  combo: HTMLElement;
  stanceBadge: HTMLElement;
  inkRow: HTMLElement;
  inkMeter: HTMLElement;
  chain: HTMLElement;
  anchor: HTMLElement;
  view: FighterView;
  icon: Sprite;
  maxHp: number;
  hp: number;
}

interface StanceButton {
  stance: PickStance;
  el: HTMLButtonElement;
  cd: HTMLElement;
}

/** SCREEN 03 — turn-based rock-paper-scissors battle (layout: docs/art/reference/03-battle.webp). */
export class BattleScene extends Scene<BattleParams> {
  private driver!: BattleDriver;
  private params!: BattleParams;
  private hero: Hero | null = null;
  private monster: Monster | null = null;
  /** My moves this battle, and whether the "it read your habit" hint was already shown. */
  private myMoves: string[] = [];
  private habitHinted = false;
  private sides!: [Side, Side];
  private particles = new ParticlePool(70);
  private numbers = new FloatingNumbers(12);
  private impact!: Sprite;
  private clip = new Graphics();
  private buttons: HTMLButtonElement[] = [];
  private stanceButtons: StanceButton[] = [];
  /** Class ultimate, unlocked by a full ink gauge. */
  private ultBtn!: { el: HTMLButtonElement; fill: HTMLElement };
  /** Single-player lucky dice button (absent in online battles). */
  private diceBtn: { el: HTMLButtonElement; count: HTMLElement } | null = null;
  private roundBadge!: HTMLElement;
  private status!: HTMLElement;
  /** The monster's tell and the page event for the round being chosen (single player). */
  private previewEl!: HTMLElement;
  /** The preview already drawn (the hand buttons are shuffled once per round). */
  private shownPreview: RoundPreview | null = null;
  private timerEl!: HTMLElement;
  private logList!: HTMLOListElement;
  private queue: Promise<void> = Promise.resolve();
  private phase: 'choose' | 'waiting' | 'animating' | 'over' = 'choose';
  /** How the battle ended, known before its closing animation plays. */
  private ended: BattleEnd | null = null;
  private myMove: Move | null = null;
  private myStance: Stance = 'none';
  private stats = { roundWinsByMove: { rock: 0, paper: 0, scissors: 0 } as Record<Move, number>, bestCombo: 0 };
  private timerInterval = 0;
  private layoutFrame = 0;
  private shake = { x: 0, y: 0 };

  constructor(app: App) {
    super(app, 'scene-battle page');
  }

  enter(params: BattleParams): void {
    const app = this.app;
    this.params = params;
    let leftArt;
    let rightArt;
    let leftLook: string;
    let rightLook: string;
    let portraits: [HTMLCanvasElement, HTMLCanvasElement];

    if (params.mode === 'pve') {
      const hero = app.store.hero;
      const monster = findMonster(app.store.state.monsters, params.monsterId);
      if (!hero || !monster) throw new Error('missing hero or monster');
      this.hero = hero;
      this.monster = monster;
      this.myMoves = [];
      this.habitHinted = false;
      // Written to disk at once: reloading mid-fight is then charged as a loss on the next start.
      app.store.update([], (s) => markBattleStart(s, hero, monster));
      app.store.flush();
      this.driver = new PveDriver(heroCombat(hero, app.store.state.itemLevels), { name: monster.name, maxHp: monster.hp, atk: monster.atk, def: monster.def, luck: monster.luck, skill: monster.skill }, monster.personality, aiLevelOf(monster), isBossMonster(monster));
      const monsterId = monsterArt(monster) ?? 'monster:gum_blob';
      leftArt = acquireArt(app, heroDrawing(hero), heroArtId(hero));
      rightArt = acquireArt(app, monster.drawing, monsterId);
      leftLook = t(heroClass(hero.classId).name);
      rightLook = t(tierDef(monster.tier).name);
      portraits = [thumb(app, heroDrawing(hero), heroArtId(hero), 64), thumb(app, monster.drawing, monsterId, 64)];
    } else {
      this.driver = new PvpDriver(app.mp, app.room);
      const b = app.room.battle!;
      const me = b.players[app.room.mySeat]!;
      const them = b.players[app.room.mySeat === 0 ? 1 : 0]!;
      const art = (look: typeof me.fighter.look, kind: string): string => (look.preset ? `${kind === 'hero' ? 'hero' : 'monster'}:${look.preset}` : 'monster:gum_blob');
      leftArt = acquireArt(app, null, art(me.fighter.look, me.fighter.kind));
      rightArt = acquireArt(app, null, art(them.fighter.look, them.fighter.kind));
      portraits = [thumb(app, null, art(me.fighter.look, me.fighter.kind), 64), thumb(app, null, art(them.fighter.look, them.fighter.kind), 64)];
      if (me.fighter.look.drawingId) void this.loadDrawing(0, me.fighter.look.drawingId);
      if (them.fighter.look.drawingId) void this.loadDrawing(1, them.fighter.look.drawingId);
      leftLook = me.name;
      rightLook = them.name;
    }
    this.d.add(leftArt.release);
    this.d.add(rightArt.release);
    this.d.add(() => this.driver.dispose());
    this.d.add(() => window.clearInterval(this.timerInterval));
    this.d.add(() => cancelAnimationFrame(this.layoutFrame));

    const [fa, fb] = this.driver.fighters;
    const makeSide = (i: 0 | 1): Side => {
      const f = i === 0 ? fa : fb;
      const hpMeter = meter(f.maxHp, f.maxHp, 'hp');
      const hpText = h('span', { class: 'hp-text' }, `${f.maxHp} / ${f.maxHp}`);
      const combo = h('span', { class: 'combo-badge', 'aria-live': 'polite' });
      const stanceBadge = h('span', { class: 'bt-stance-badge', 'aria-live': 'polite' });
      const portrait = portraits[i];
      const inkMeter = meter(0, RULES.ink.max, 'stat');
      inkMeter.classList.add('meter-ink');
      const chain = h('span', { class: 'chain-pips', title: t('Combo chain: win {n} rounds in a row with different hands for a bonus hit!', { n: RULES.chain.length }) });
      const inkRow = h('div', { class: 'ink-row', title: ultimateTip(f.skill) }, icon('drop', 'ink-drop'), inkMeter, chain);
      const chip = (ic: keyof typeof ICONS, label: string, v: number): HTMLElement =>
        h('span', { class: `bt-chip ${ic}`, title: label, 'aria-label': `${label} ${v}` }, icon(ic), h('b', null, String(v)));
      const card = h(
        'div',
        { class: `fighter-card bt-panel ${i === 0 ? 'left bt-me' : 'right bt-foe'}` },
        h('div', { class: 'bt-portrait' }, portrait, icon(i === 0 ? 'crown' : 'skull', 'bt-portrait-badge')),
        h(
          'div',
          { class: 'bt-panel-body' },
          h('div', { class: 'fighter-name' }, h('b', null, this.nameOf(i)), h('small', null, i === 0 ? (params.mode === 'pvp' ? t('you') : leftLook) : rightLook)),
          h('div', { class: 'hp-row' }, icon('heart', 'bt-hp-heart'), h('span', { class: 'hp-label' }, 'HP'), hpText),
          hpMeter,
          inkRow,
          h(
            'div',
            { class: 'bt-chips' },
            chip('sword', 'ATK', f.atk),
            chip('shield', 'DEF', f.def),
            chip('clover', 'LUCK', f.luck),
            h('span', { class: 'skill-tag', title: t(skillDef(f.skill).description) }, `★ ${t(skillDef(f.skill).name)}`),
          ),
        ),
        combo,
        stanceBadge,
      );
      const anchor = h('div', { class: `stage-anchor bt-anchor ${i === 0 ? 'left' : 'right'}` });
      const art = i === 0 ? leftArt : rightArt;
      const view = new FighterView(art, i === 0 ? 1 : -1, app.tweens);
      const moveIcon = new Sprite(app.assets.get('move:rock')[0]);
      moveIcon.anchor.set(0.5);
      moveIcon.visible = false;
      return { card, portrait, hpMeter, hpText, combo, stanceBadge, inkRow, inkMeter, chain, anchor, view, icon: moveIcon, maxHp: f.maxHp, hp: f.maxHp };
    };
    this.sides = [makeSide(0), makeSide(1)];
    this.renderInk(this.driver.ink, this.driver.chain);
    this.impact = new Sprite(app.assets.get('fx:pow')[0]);
    this.impact.anchor.set(0.5);
    this.impact.visible = false;

    this.roundBadge = h('div', { class: 'round-badge bt-ribbon' }, `${t('Round')} `, h('b', null, '1'));
    this.status = h('p', { class: 'battle-status', 'aria-live': 'polite' });
    // Lives in the overlay layer so the tell floats above the Pixi fighters (placed by placePreview).
    this.previewEl = h('div', { class: 'bt-preview', 'aria-live': 'polite' });
    document.getElementById('overlay-layer')?.appendChild(this.previewEl);
    this.d.add(() => this.previewEl.remove());
    this.timerEl = h('div', { class: 'turn-timer', hidden: params.mode !== 'pvp' });
    this.logList = h('ol', { class: 'log-list' });

    this.buttons = MOVES.map((m, i) => {
      // Pack button art (normal / hover / pressed / disabled); CSS shows the right one.
      const states = ['normal', 'hover', 'pressed', 'disabled'] as const;
      const urls = states.map((st) => app.assets.url(`button:${m}`, st));
      let art: HTMLElement;
      if (urls.every(Boolean)) {
        art = h('span', { class: 'rps-art', 'aria-hidden': 'true' }, states.map((st, k) => h('img', { class: `rps-art-${st}`, src: urls[k]!, alt: '', draggable: false })));
      } else {
        const c = h('canvas', { width: 160, height: 160, class: 'rps-icon', 'aria-hidden': 'true' });
        app.assets.drawThumb(c, `move:${m}`);
        art = c;
      }
      return h(
        'button',
        { class: `rps-btn rps-${m} ${urls.every(Boolean) ? 'has-art' : ''}`, type: 'button', 'aria-label': t('{move} (key {key})', { move: t(MOVE_LABEL[m]), key: i + 1 }), on: { click: () => this.choose(m) } },
        art,
        h('span', { class: 'rps-name' }, t(MOVE_LABEL[m])),
        h('kbd', null, String(i + 1)),
      );
    });

    this.stanceButtons = (['guard', 'focus'] as const).map((s) => {
      const cd = h('span', { class: 'stance-cd', 'aria-hidden': 'true' });
      const el = h(
        'button',
        {
          class: `stance-btn stance-${s}`,
          type: 'button',
          'aria-pressed': 'false',
          title: stanceTip(s),
          on: { click: () => this.toggleStance(s) },
        },
        icon(s === 'guard' ? 'shield' : 'swirl', 'stance-icon'),
        h('span', { class: 'stance-name' }, t(STANCE_LABEL[s])),
        h('kbd', null, s === 'guard' ? 'G' : 'F'),
        cd,
      );
      return { stance: s, el, cd };
    });

    {
      const fill = h('span', { class: 'ult-fill', 'aria-hidden': 'true' });
      const el = h(
        'button',
        { class: 'stance-btn ult-btn', type: 'button', 'aria-pressed': 'false', title: ultimateTip(fa.skill), on: { click: () => this.toggleUltimate() } },
        fill,
        h('span', { class: 'ult-spark', 'aria-hidden': 'true' }, '✨'),
        h('span', { class: 'stance-name' }, t(ULT_NAME[fa.skill] ?? ULT_NAME.none)),
        h('kbd', null, 'U'),
      );
      this.ultBtn = { el, fill };
    }

    if (this.driver instanceof PveDriver) {
      const count = h('span', { class: 'stance-cd dice-count', 'aria-hidden': 'true' });
      const el = h(
        'button',
        { class: 'stance-btn dice-btn', type: 'button', title: t('Lucky dice: heal, or hurt the monster. Win 3 rounds in a row for another die.'), on: { click: () => this.rollDice() } },
        h('span', { class: 'dice-face', 'aria-hidden': 'true' }, '🎲'),
        h('span', { class: 'stance-name' }, t('Dice')),
        h('kbd', null, 'D'),
        count,
      );
      this.diceBtn = { el, count };
    }

    const runBtn = button(t('Run away'), { variant: 'ghost', small: true, sfx: app, onClick: () => void this.runAway() });
    runBtn.classList.add('bt-run');
    const stanceTips = h(
      'p',
      { class: 'bt-hint-stances' },
      icon('shield'),
      ` ${stanceTip('guard')}`,
      h('br'),
      icon('swirl'),
      ` ${stanceTip('focus')}`,
      h('br'),
      '✨ ',
      ultimateTip(fa.skill),
      h('br'),
      '✊✋✌️ ',
      t('Combo chain: win {n} rounds in a row with different hands for a bonus hit!', { n: RULES.chain.length }),
    );

    const hint = h('aside', { class: 'bt-hint paper-scrap' },
      h('p', { class: 'bt-hint-title' }, sharedIcon('bulb', 'bt-icon'), ` ${t('Hint:')}`),
      h('p', null, t('Rock beats Scissors, Scissors beats Paper, Paper beats Rock!')),
      h('details', null, h('summary', null, t('Guard / Focus / Ultimate')), stanceTips),
    );
    this.dom.append(
      h('div', { class: 'spiral', 'aria-hidden': 'true' }),
      deskProps(),
      this.sides[0].card,
      h(
        'div',
        { class: 'bt-title' },
        h('div', { class: 'bt-title-row' }, icon('sword', 'bt-sword left'), h('h1', { class: 'bt-title-text' }, t('Battle')), icon('sword', 'bt-sword right')),
        h('div', { class: 'bt-round-row' }, this.roundBadge, this.timerEl),
      ),
      this.sides[1].card,
      paperScenery('arena bt-arena'),
      h('div', { class: 'bt-left' }, h('div', { class: 'sticky-note yellow bt-tip' }, t('Draw your choice and beat the monsters!'), icon('smile')), runBtn),
      h('div', { class: 'bt-stage' }, h('div', { class: 'bt-fighters' }, this.sides[0].anchor, this.sides[1].anchor), this.status),
      h('p', { class: 'bt-margin-note', 'aria-hidden': 'true' }, t('Monsters erase… But legends are drawn!'), sharedIcon('crown')),
      h('section', { class: 'bt-log battle-log' }, h('h3', null, icon('book'), ` ${t('Battle Log')}`), this.logList),
      h(
        'div',
        { class: 'bt-controls' },
        h('div', { class: 'rps-bar', role: 'group', 'aria-label': t('Choose your move') }, this.buttons),
        h('div', { class: 'stance-bar', role: 'group', 'aria-label': t('Optional stance (pick before your move)') }, this.ultBtn.el, this.stanceButtons.map((b) => b.el), this.diceBtn?.el ?? null),
      ),
      h('div', { class: 'bt-extras' }, hint, this.rewardsCard()),
    );

    this.clip.label = 'battle-clip';
    this.root.addChild(this.clip, this.sides[0].view, this.sides[1].view, this.sides[0].icon, this.sides[1].icon, this.impact, this.particles, this.numbers);
    this.root.mask = this.clip;

    this.driver.on('round', (r) => (this.queue = this.queue.then(() => this.playRound(r))));
    this.driver.on('end', (e) => {
      this.ended = e;
      // Won or drawn: a reload during the closing animation must not count as running away.
      if (e.outcome !== 'them' && this.app.store.state.battleInProgress) {
        this.app.store.update([], (st) => (st.battleInProgress = null));
        this.app.store.flush();
      }
      this.queue = this.queue.then(() => this.playEnd(e));
    });
    this.driver.on('dice', (d) => (this.queue = this.queue.then(() => this.playDice(d))));
    this.driver.on('opponentLocked', () => {
      if (this.phase !== 'over') this.setStatus(t('{name} has locked in a move… (it stays secret!)', { name: this.nameOf(1) }));
    });
    this.driver.on('error', (msg) => {
      app.toast(msg, 'error');
      if (this.phase === 'waiting') this.setPhase('choose');
    });
    this.driver.on('resync', (s) => {
      this.queue = this.queue.then(() => {
        s.hp.forEach((v, i) => this.setHp(i as 0 | 1, v));
        this.renderInk(this.driver.ink, this.driver.chain);
        this.roundBadge.querySelector('b')!.textContent = String(s.round);
        this.setPhase(s.myLocked ? 'waiting' : 'choose');
      });
    });

    this.d.listen(window, 'keydown', (e: Event) => {
      const ke = e as KeyboardEvent;
      if (ke.target instanceof HTMLInputElement || ke.target instanceof HTMLTextAreaElement) return;
      if (ke.ctrlKey || ke.metaKey || ke.altKey) return;
      const key = ke.key.toLowerCase();
      const m = KEYS[key];
      if (m) this.choose(m);
      const s = STANCE_KEYS[key];
      if (s) this.toggleStance(s);
      if (key === 'u') this.toggleUltimate();
      if (key === 'd') this.rollDice();
    });
    // Anchors move when the page scrolls (phones) or the panels change size.
    const relayout = (): void => {
      cancelAnimationFrame(this.layoutFrame);
      this.layoutFrame = requestAnimationFrame(() => this.layout(this.app.viewport.info));
    };
    this.d.listen(this.dom, 'scroll', relayout, { passive: true });
    if (typeof ResizeObserver !== 'undefined') {
      const ro = new ResizeObserver(relayout);
      ro.observe(this.sides[0].anchor);
      ro.observe(this.dom);
      this.d.add(() => ro.disconnect());
    }
    if (params.mode === 'pvp') this.timerInterval = window.setInterval(() => this.renderTimer(), 250);
    this.setPhase('choose');
  }

  /** Bottom-right card: what a win is worth (single player) or what kind of online battle this is. */
  private rewardsCard(): HTMLElement {
    const card = h('aside', { class: 'bt-rewards paper-scrap' });
    const m = this.monster;
    if (this.params.mode === 'pvp') {
      const b = this.app.room.battle;
      card.append(
        h('h3', null, icon('star'), ` ${t('Online battle')}`),
        h('p', null, b?.ranked ? t('Ranked: win for trophies!') : t('Classroom battle — not ranked.')),
        h('p', { class: 'hand-hint' }, t('Results are decided by the server.')),
      );
      return card;
    }
    if (!m) return card;
    card.append(h('h3', null, t('Rewards'), ' ', h('small', null, t('(preview)'))));
    if (m.mode === 'classroom') {
      card.append(h('p', { class: 'hand-hint' }, t('Practice battle: no rewards.')));
      return card;
    }
    const save = this.app.store.state;
    const preset = m.source === 'preset' && m.preset ? PRESET_MONSTERS.find((p) => p.id === m.preset) : undefined;
    const itemId = preset && preset.firstWinItem && !save.defeatedPresets.includes(preset.id) && !save.items.includes(preset.firstWinItem) ? preset.firstWinItem : null;
    const item = itemId ? itemDef(itemId) : undefined;
    const row = h(
      'div',
      { class: 'bt-reward-row' },
      h('div', { class: 'bt-reward' }, icon('star', 'bt-reward-icon'), h('b', null, `+${m.reward.exp}`), h('small', null, 'EXP')),
      h('div', { class: 'bt-reward' }, sharedIcon('coin', 'bt-reward-coin'), h('b', null, `+${m.reward.coins}`), h('small', null, t('coins'))),
      itemId
        ? h(
            'div',
            { class: 'bt-reward', title: item ? t(item.name) : itemId },
            artImg(this.app, `item:${itemId}`, 'bt-reward-item', icon('gift', 'bt-reward-icon')),
            h('b', null, '+1'),
            h('small', null, item ? t(item.name) : itemId),
          )
        : null,
    );
    card.append(row);
    const best = preset ? (save.monsterStars?.[preset.id] ?? 0) : 0;
    card.append(
      h(
        'p',
        { class: 'bt-reward-foot' },
        itemId ? t('Defeat to get these! (first win: {item})', { item: item ? t(item.name) : itemId }) : t('Defeat to get these!'),
        best > 0 ? h('span', { class: 'bt-best-stars', title: t('Best rating') }, ` ${starText(best)}`) : null,
      ),
    );
    return card;
  }

  /** Swap a placeholder doodle for the player's uploaded drawing. */
  private async loadDrawing(side: 0 | 1, id: string): Promise<void> {
    try {
      const d = await this.app.api.getDrawing(id);
      if (this.isDestroyed) return;
      const art = acquireArt(this.app, d, 'monster:gum_blob');
      this.d.add(art.release);
      this.sides[side].view.setArt(art);
      drawThumbnail(this.sides[side].portrait, d);
      this.layout(this.app.viewport.info);
    } catch {
      /* keep the placeholder doodle */
    }
  }

  override shown(): void {
    void this.sides[0].view.reveal();
    void this.sides[1].view.reveal();
    const foe = this.nameOf(1);
    this.log(this.driver.kind === 'pvp' ? t('{name} steps up for an online battle!', { name: foe }) : t('A wild {name} appears!', { name: foe }));
    this.layout(this.app.viewport.info);
    this.buttons[0]?.focus({ preventScroll: true });
  }

  /** Display name of a fighter: preset monster names are translated, player-entered names never. */
  private nameOf(i: 0 | 1): string {
    const name = this.driver.fighters[i].name;
    return i === 1 && this.monster && hasBuiltInName(this.monster) ? t(name) : name;
  }

  override layout(_vp: ViewportInfo): void {
    if (!this.sides) return;
    this.placePreview();
    for (const s of this.sides) {
      const a = this.anchor(s.anchor);
      s.view.position.set(a.cx, a.y + a.h);
      s.view.setHeight(a.h);
      s.icon.scale.set(Math.max(0.35, a.h / 380) * nominalScale(s.icon.texture, 128));
    }
    // Clip the doodles to the page, so nothing floats over the desk when a phone scrolls.
    const r = this.anchor(this.dom);
    this.clip.clear().rect(r.x, r.y, r.w, r.h).fill({ color: 0xffffff });
  }

  override update(dt: number): void {
    this.sides[0].view.update(dt);
    this.sides[1].view.update(dt);
    this.particles.update(dt);
    this.numbers.update(dt);
    this.root.position.set(this.shake.x, this.shake.y);
    this.clip.position.set(-this.shake.x, -this.shake.y);
  }

  private setStatus(text: string): void {
    this.status.textContent = text;
  }

  /** Prompt for the choose phase (mentions a picked stance). */
  private choosePrompt(): string {
    if (this.myStance === 'ultimate') return t('✨ {name} ready — now pick Rock, Paper or Scissors!', { name: t(ULT_NAME[this.driver.fighters[0].skill] ?? ULT_NAME.none) });
    if (this.myStance !== 'none') return t('{stance} ready — now pick Rock, Paper or Scissors!', { stance: t(STANCE_LABEL[this.myStance]) });
    if (ultimateReady(this.driver.ink[0])) return t('✨ Your ink is full! Press Ultimate (U), then pick your hand.');
    return this.driver.kind === 'pve'
      ? t('{name} has secretly picked a move. Your turn — Rock, Paper or Scissors?', { name: this.nameOf(1) })
      : t('Pick your move before the timer runs out!');
  }

  private setPhase(p: BattleScene['phase']): void {
    this.phase = p;
    const enabled = p === 'choose';
    this.buttons.forEach((b) => {
      b.disabled = !enabled;
      b.classList.toggle('picked', !enabled && this.myMove !== null && b.classList.contains(`rps-${this.myMove}`));
    });
    if (p === 'choose') {
      this.myMove = null;
      this.myStance = 'none';
      this.buttons.forEach((b) => b.classList.remove('picked'));
      this.setStatus(this.choosePrompt());
      this.renderPreview();
    } else if (p === 'waiting') {
      this.setStatus(t('Locked in! Waiting for {name}…', { name: this.nameOf(1) }));
    }
    this.renderStances();
  }

  /** Stance buttons: selected state, cooldown number, enabled only while choosing. */
  private renderStances(): void {
    const cd = this.driver.cooldown[0];
    for (const b of this.stanceButtons) {
      const selected = this.myStance === b.stance;
      b.el.disabled = this.phase !== 'choose' || cd > 0;
      b.el.classList.toggle('selected', selected);
      b.el.classList.toggle('cooling', cd > 0);
      b.el.setAttribute('aria-pressed', String(selected));
      b.cd.textContent = cd > 0 ? String(cd) : '';
      b.el.setAttribute('aria-label', cd > 0 ? t('{stance} (key {key}) — ready in {n} rounds', { stance: t(STANCE_LABEL[b.stance]), key: b.stance === 'guard' ? 'G' : 'F', n: cd }) : t('{stance} (key {key})', { stance: t(STANCE_LABEL[b.stance]), key: b.stance === 'guard' ? 'G' : 'F' }));
    }
    const ready = ultimateReady(this.driver.ink[0]);
    const picked = this.myStance === 'ultimate';
    this.ultBtn.el.disabled = this.phase !== 'choose' || !ready;
    this.ultBtn.el.classList.toggle('ready', ready);
    this.ultBtn.el.classList.toggle('selected', picked);
    this.ultBtn.el.setAttribute('aria-pressed', String(picked));
    this.ultBtn.fill.style.width = `${Math.min(100, (this.driver.ink[0] * 100) / RULES.ink.max)}%`;
    this.ultBtn.el.setAttribute('aria-label', ready ? t('Ultimate (key U): ready!') : t('Ultimate (key U): ink {n} / {max}', { n: this.driver.ink[0], max: RULES.ink.max }));
    if (this.diceBtn && this.driver instanceof PveDriver) {
      const n = this.driver.diceCharges;
      this.diceBtn.el.disabled = this.phase !== 'choose' || n <= 0;
      this.diceBtn.count.textContent = String(n);
      this.diceBtn.el.setAttribute('aria-label', t('Lucky dice (key D): {n} left', { n }));
    }
  }

  /** Rolls a lucky die between rounds (single-player only). */
  private rollDice(): void {
    if (this.phase !== 'choose' || !(this.driver instanceof PveDriver) || this.driver.diceCharges <= 0) return;
    this.phase = 'animating';
    this.renderStances();
    this.driver.rollDice();
  }

  private async playDice(d: DiceRollResult & { charges: number }): Promise<void> {
    if (this.isDestroyed) return;
    const face = this.diceBtn?.el.querySelector('.dice-face');
    // A quick tumble through random faces before it lands.
    const faces = ['⚀', '⚁', '⚂', '⚃', '⚄', '⚅'];
    this.app.sfx.play('flip');
    for (let i = 0; i < 6 && face; i++) {
      face.textContent = faces[Math.floor(Math.random() * 6)]!;
      await this.app.tweens.wait(70);
      if (this.isDestroyed) return;
    }
    if (face) face.textContent = faces[d.face - 1]!;
    // A healing roll at full HP would read 'Heal 0 HP'.
    const text = d.face !== 1 && d.heal === 0 && d.damage === 0 ? t('Already at full HP — the plaster is saved for later!') : t(BATTLE_DICE[d.face].text, { heal: d.heal, damage: d.damage });
    this.app.sfx.play(d.face === 6 ? 'crit' : d.damage > 0 ? 'hit' : d.heal > 0 ? 'heal' : 'miss');
    if (d.heal > 0) this.setHp(0, d.hp[0]);
    if (d.damage > 0) {
      this.setHp(1, d.hp[1]);
      void this.sides[1].view.hit(d.face === 6);
    }
    this.log(`🎲 ${d.face} — ${text}`, d.face === 6 ? 'crit' : '');
    this.setStatus(`🎲 ${d.face}! ${text}`);
    await this.app.tweens.wait(600);
    if (this.isDestroyed) return;
    if (face) face.textContent = '🎲';
    if (d.hp[1] > 0) this.setPhase('choose');
  }

  private toggleUltimate(): void {
    if (this.phase !== 'choose' || !ultimateReady(this.driver.ink[0])) return;
    this.myStance = this.myStance === 'ultimate' ? 'none' : 'ultimate';
    this.app.sfx.play('click');
    this.renderStances();
    this.setStatus(this.choosePrompt());
  }

  /** Ink gauges and combo chain pips (index 0 = me). */
  private renderInk(ink: readonly [number, number], chain: readonly [readonly Move[], readonly Move[]]): void {
    for (const i of [0, 1] as const) {
      const s = this.sides[i];
      setMeter(s.inkMeter, ink[i], RULES.ink.max);
      s.inkMeter.setAttribute('aria-label', t('Ink {n} / {max}', { n: ink[i], max: RULES.ink.max }));
      s.inkRow.classList.toggle('full', ultimateReady(ink[i]));
      const pips: HTMLElement[] = [];
      for (let k = 0; k < RULES.chain.length; k++) {
        const m = chain[i][k];
        pips.push(h('span', { class: `chain-pip ${m ? 'on' : ''}` }, m ? MOVE_EMOJI[m] : ''));
      }
      setChildren(s.chain, pips);
    }
  }

  private toggleStance(s: PickStance): void {
    if (this.phase !== 'choose' || this.driver.cooldown[0] > 0) return;
    this.myStance = this.myStance === s ? 'none' : s;
    this.app.sfx.play('click');
    this.renderStances();
    this.setStatus(this.choosePrompt());
  }

  private choose(move: Move): void {
    if (this.phase !== 'choose') return;
    this.myMove = move;
    const stance = this.myStance;
    this.app.sfx.play('lock');
    this.phase = 'waiting';
    this.buttons.forEach((b) => {
      b.disabled = true;
      b.classList.toggle('picked', b.classList.contains(`rps-${move}`));
    });
    this.setStatus(t('Locked in! Waiting for {name}…', { name: this.nameOf(1) }));
    this.renderStances();
    this.driver.submit(move, stance).catch(() => {
      /* error event resets the phase */
    });
  }

  private renderTimer(): void {
    if (this.driver.kind !== 'pvp' || this.phase === 'over') {
      this.timerEl.textContent = '';
      return;
    }
    const left = Math.max(0, Math.ceil((this.driver.deadline - Date.now()) / 1000));
    this.timerEl.textContent = t('⏱ {n}s', { n: left });
    this.timerEl.classList.toggle('urgent', left <= 5);
  }

  private setHp(i: 0 | 1, hp: number): void {
    const s = this.sides[i];
    s.hp = hp;
    setMeter(s.hpMeter, hp, s.maxHp);
    s.hpText.textContent = `${hp} / ${s.maxHp}`;
    s.card.classList.toggle('low', hp / s.maxHp < 0.3);
  }

  private log(text: string, cls = ''): void {
    this.logList.prepend(h('li', { class: cls }, text));
    while (this.logList.childElementCount > 40) this.logList.lastElementChild?.remove();
  }

  /** Panel badge + log line + floating word for the stances applied this round. */
  private showStances(r: LocalRound, names: string[]): void {
    for (const i of [0, 1] as const) {
      const st = r.stances[i];
      const s = this.sides[i];
      s.stanceBadge.className = `bt-stance-badge ${st}`;
      s.stanceBadge.textContent = '';
      if (st === 'none') continue;
      if (st === 'ultimate') {
        s.stanceBadge.append(`✨ ${t('Ultimate!')}`);
        void s.stanceBadge.offsetWidth;
        s.stanceBadge.classList.add('pop');
        continue;
      }
      s.stanceBadge.append(icon(st === 'guard' ? 'shield' : 'swirl'), ` ${st === 'guard' ? t('Guarding!') : t('Focused!')}`);
      void s.stanceBadge.offsetWidth;
      s.stanceBadge.classList.add('pop');
      this.log(st === 'guard' ? t('{name} is guarding!', { name: names[i]! }) : t('{name} is focused!', { name: names[i]! }), `stance ${st}`);
      this.numbers.show(st === 'guard' ? 'GUARD' : 'FOCUS!', s.view.x, s.view.y - this.anchor(s.anchor).h - 10, st === 'guard' ? 0x2e86de : 0x8e5bd6, 34);
    }
  }

  private async showMoves(r: LocalRound): Promise<void> {
    const tw = this.app.tweens;
    this.sides.forEach((s, i) => {
      const m = r.moves[i]!;
      if (m === 'timeout') {
        s.icon.visible = false;
        return;
      }
      s.icon.texture = this.app.assets.get(`move:${m}`)[0]!;
      // The hand sign pops up in front of the fighter, towards the opponent.
      const ah = this.anchor(s.anchor).h;
      const top = s.view.y - ah * 0.82;
      s.icon.position.set(s.view.x + (i === 0 ? 1 : -1) * ah * 0.42, top + 20);
      s.icon.alpha = 0;
      s.icon.rotation = (i === 0 ? -1 : 1) * 0.2;
      s.icon.visible = true;
      void tw.to(s.icon, { alpha: 1, y: top, rotation: 0 }, 260, { ease: Ease.outBack });
    });
    this.app.sfx.play('pop');
    await tw.wait(motion.reduced ? 250 : 520);
    if (this.isDestroyed) return;
  }

  private hideMoves(): void {
    for (const s of this.sides) void this.app.tweens.to(s.icon, { alpha: 0 }, 180).then(() => !s.icon.destroyed && (s.icon.visible = false));
  }

  private async screenShake(power: number): Promise<void> {
    if (motion.reduced) return;
    const tw = this.app.tweens;
    for (let i = 0; i < 4; i++) {
      await tw.to(this.shake, { x: (Math.random() - 0.5) * power, y: (Math.random() - 0.5) * power }, 35, { ease: Ease.linear });
      if (this.isDestroyed) return;
    }
    await tw.to(this.shake, { x: 0, y: 0 }, 60);
    if (this.isDestroyed) return;
  }

  /** Sharp story and tower monsters counter a repeated move: say so once, so spamming one move is a lesson, not a mystery. */
  private hintHabit(r: LocalRound, monsterName: string): void {
    this.myMoves.push(r.moves[0]);
    const last = this.myMoves.slice(-3);
    if (this.habitHinted || this.params.mode !== 'pve' || !this.monster || aiLevelOf(this.monster) === 0) return;
    if (r.winner !== 1 || last.length < 3 || last.some((m) => m !== r.moves[0])) return;
    this.habitHinted = true;
    const text = t('👀 {name} spotted your habit! It counters a move you keep repeating — mix up Rock, Paper and Scissors.', { name: monsterName });
    this.log(text, 'tie');
    this.setStatus(text);
  }

  private async playRound(r: LocalRound): Promise<void> {
    this.phase = 'animating';
    this.previewEl.classList.add('resolving');
    this.buttons.forEach((b) => (b.disabled = true));
    this.renderStances();
    await this.showMoves(r);
    if (this.isDestroyed) return;
    const names = [this.nameOf(0), this.nameOf(1)];
    const mv = t('{a} vs {b}', { a: t(MOVE_LABEL[r.moves[0]]), b: t(MOVE_LABEL[r.moves[1]]) });
    this.showStances(r, names);
    this.hintHabit(r, names[1]!);
    const ultHealed = await this.playUltimates(r, names);
    if (this.isDestroyed) return;
    const healedNow: [number, number] = [r.healed[0] - ultHealed[0], r.healed[1] - ultHealed[1]];
    const ultKo = this.sides[0].hp === 0 || this.sides[1].hp === 0;

    if (ultKo) {
      this.log(t('R{round}: {moves} — the ultimate ended it before the hands landed!', { round: r.round, moves: mv }), 'crit');
    } else if (r.winner === -1) {
      this.log(t('R{round}: {moves} — tie, no damage.', { round: r.round, moves: mv }), 'tie');
      this.numbers.show('TIE', (this.sides[0].view.x + this.sides[1].view.x) / 2, this.sides[0].view.y - 160, 0xa9c6e8, 44);
      this.app.sfx.play('miss');
      for (const i of [0, 1] as const) {
        if (healedNow[i] > 0) {
          const s = this.sides[i];
          this.numbers.show(`+${healedNow[i]}`, s.view.x, s.view.y - 120, 0x6ab04c);
          this.particles.burst(this.app.assets.get('fx:heart'), s.view.x, s.view.y - 80, 5, { gravity: -60, speed: 90, scale: 0.5 });
          this.setHp(i, r.hp[i]);
          this.app.sfx.play('heal');
          this.log(t('{name} erased some scratches (+{hp} HP).', { name: names[i]!, hp: healedNow[i] }), 'heal');
        }
      }
      await this.app.tweens.wait(450);
      if (this.isDestroyed) return;
    } else {
      const w = r.winner;
      const l = w === 0 ? 1 : 0;
      const att = this.sides[w];
      const def = this.sides[l];
      if (w === 0 && r.moves[0] !== 'timeout') {
        this.stats.roundWinsByMove[r.moves[0]]++;
        this.stats.bestCombo = Math.max(this.stats.bestCombo, r.combo[0]);
      }
      const attack = att.view.attack(Math.abs(def.view.x - att.view.x) * 0.45);
      await this.app.tweens.wait(motion.reduced ? 90 : 270);
      if (this.isDestroyed) return;
      // Impact!
      const hitX = def.view.x;
      const hitY = def.view.y - this.anchor(def.anchor).h * 0.5;
      this.impact.texture = this.app.assets.get(r.crit ? 'fx:pow' : 'fx:impact')[0]!;
      this.impact.position.set(hitX, hitY);
      const ultBlocked = r.shielded && r.damage === 0 && r.stances[l] === 'ultimate';
      this.impact.visible = !r.dodged && !ultBlocked;
      this.impact.alpha = 1;
      void this.app.tweens.to(this.impact, { alpha: 0 }, 420, { delay: 160 });
      const pop = { s: 0.3 };
      const k = nominalScale(this.impact.texture, 160);
      this.impact.scale.set(0.3 * k);
      void this.app.tweens.to(pop, { s: r.crit ? 1.3 : 0.9 }, 200, { ease: Ease.outBack, onUpdate: () => this.impact.scale.set(pop.s * k) });
      if (ultBlocked) {
        this.numbers.show('BLOCK!', hitX, hitY - 40, 0x6ab04c, 46);
        this.app.sfx.play('miss');
      } else if (r.dodged) {
        void def.view.dodge();
        this.numbers.show('DODGE!', hitX, hitY - 40, 0x2e86de, 42);
        this.app.sfx.play('miss');
      } else {
        void def.view.hit(r.crit);
        void this.screenShake(r.crit ? 16 : 7);
        this.app.sfx.play(r.crit ? 'crit' : 'hit');
        this.particles.burst([...this.app.assets.get('fx:eraser_crumb'), ...this.app.assets.get('fx:paper_scrap'), ...this.app.assets.get('fx:shaving')], hitX, hitY, r.crit ? 18 : 10, { scale: 0.55 });
        this.numbers.show(`-${r.damage}`, hitX, hitY - 50, r.crit ? 0xf3a712 : 0xe4572e, r.crit ? 64 : 50);
        if (r.crit) this.numbers.show('CRIT!', hitX, hitY - 110, 0xf6d55c, 40);
        if (r.shielded) this.numbers.show('BLOCK', hitX + 40, hitY - 10, 0x6ab04c, 34);
      }
      this.setHp(l as 0 | 1, r.hp[l]! + r.burstDamage);
      // After-hit skills: the winner drains (Highlighter Drain), the loser's spikes bite back (Sharpener Spikes).
      if (healedNow[w] > 0) {
        this.numbers.show(`+${healedNow[w]}`, att.view.x, att.view.y - 140, 0x6ab04c, 40);
        this.particles.burst(this.app.assets.get('fx:heart'), att.view.x, att.view.y - 90, 4, { gravity: -60, speed: 80, scale: 0.45 });
        this.app.sfx.play('heal');
      }
      if (r.recoil > 0) {
        this.numbers.show(`-${r.recoil}`, att.view.x, att.view.y - 120, 0xe4572e, 38);
        this.particles.burst(this.app.assets.get('fx:shaving'), att.view.x, att.view.y - 100, 6, { scale: 0.5 });
      }
      if (healedNow[w] > 0 || r.recoil > 0) this.setHp(w, r.hp[w]);
      const ws = r.stances[w];
      const ls = r.stances[l];
      const extras = [
        r.crit ? t('critical!') : '',
        r.dodged ? t('but it was dodged') : '',
        ultBlocked ? t('the ultimate shield blocked it all') : r.shielded ? t('paper shield blocked half') : '',
        ws === 'focus' ? t('focused hit') : '',
        ws === 'guard' ? t('careful hit') : '',
        ls === 'guard' ? t('guard blocked half') : '',
        ls === 'focus' ? t('caught off guard') : '',
        healedNow[w] > 0 ? t('highlighter drained +{hp} HP', { hp: healedNow[w] }) : '',
        r.recoil > 0 ? t('sharpener spikes hit back for {n}', { n: r.recoil }) : '',
      ]
        .filter(Boolean)
        .join(', ');
      const vars = { round: r.round, moves: mv, name: names[w]!, damage: r.damage };
      const line = r.dodged || ultBlocked ? t('R{round}: {moves} — {name} wins', vars) : t('R{round}: {moves} — {name} wins, {damage} dmg', vars);
      this.log(extras ? t('{line} ({extras}).', { line, extras }) : t('{line}.', { line }), w === 0 ? 'win' : 'loss');
      await attack;
      if (this.isDestroyed) return;
      if (r.burst !== null) {
        await this.playBurst(r, names);
        if (this.isDestroyed) return;
      }
    }
    if (r.twist) {
      await this.playTwist(r);
      if (this.isDestroyed) return;
    }
    // Everything is settled: make sure both bars match the engine / server.
    const hp = r.twist?.hp ?? r.hp;
    this.setHp(0, hp[0]);
    this.setHp(1, hp[1]);
    this.renderInk(r.twist ? [r.ink[0] + r.twist.ink[0], r.ink[1] + r.twist.ink[1]] : r.ink, r.chain);

    for (const i of [0, 1] as const) {
      const c = r.combo[i];
      const el = this.sides[i].combo;
      el.textContent = c >= 2 ? t('COMBO ×{n}', { n: c }) : '';
      el.classList.remove('pop');
      if (c >= 2) {
        void el.offsetWidth;
        el.classList.add('pop');
        if (i === 0) this.log(t('Combo ×{n}!', { n: c }), 'combo');
      }
    }
    this.hideMoves();
    this.roundBadge.querySelector('b')!.textContent = String(r.round + 1);
    if (this.inkStrikeDue(r)) {
      await this.playInkStrike();
      if (this.isDestroyed) return;
    }
    // A battle that just ended (also by an Ink Strike) goes straight to its closing scene.
    if ((this.phase as string) !== 'over' && !this.ended) this.setPhase('choose');
  }

  /** Sometimes, after a round the hero won and the monster still stands: an Ink Strike. */
  private inkStrikeDue(r: LocalRound): boolean {
    return this.driver instanceof PveDriver && !this.ended && r.winner === 0 && this.sides[1].hp > 0 && this.app.store.state.settings.traceGame && Math.random() * 100 < TWISTS.trace.chance;
  }

  /** The trace mini-game, then its extra hit. */
  private async playInkStrike(): Promise<void> {
    if (!(this.driver instanceof PveDriver)) return;
    this.setStatus(t('✏️ Ink Strike! Trace the shape for an extra hit.'));
    const score = await traceGame(this.app, TWISTS.trace.seconds);
    if (this.isDestroyed) return;
    const damage = traceDamage(this.driver.fighters[1].maxHp, score);
    const foe = this.sides[1];
    if (damage <= 0) {
      this.log(t('✏️ Ink Strike missed ({n}%).', { n: Math.round(score * 100) }), '');
      return;
    }
    const hp = this.driver.inkStrike(damage);
    const hitY = foe.view.y - this.anchor(foe.anchor).h * 0.5;
    void foe.view.hit(score >= 0.8);
    void this.screenShake(score >= 0.8 ? 14 : 8);
    this.app.sfx.play(score >= 0.8 ? 'crit' : 'hit');
    this.particles.burst(this.app.assets.get('fx:shaving'), foe.view.x, hitY, 12, { scale: 0.55 });
    this.numbers.show(`✏️ -${damage}`, foe.view.x, hitY - 50, 0x2e86de, score >= 0.8 ? 62 : 50);
    this.setHp(1, hp[1]);
    this.log(t('✏️ Ink Strike {n}%: {damage} extra damage!', { n: Math.round(score * 100), damage }), 'win');
    await this.app.tweens.wait(motion.reduced ? 150 : 400);
  }

  /** Ultimates land before the hands: flash, big name, damage and heals. Returns the HP each side healed. */
  private async playUltimates(r: LocalRound, names: string[]): Promise<[number, number]> {
    const healed: [number, number] = [0, 0];
    for (const i of [0, 1] as const) {
      if (r.stances[i] !== 'ultimate') continue;
      const me = this.sides[i];
      const foe = this.sides[i === 0 ? 1 : 0];
      const skill = this.driver.fighters[i].skill;
      const u = RULES.ultimates[skill] ?? RULES.ultimates.none;
      const name = t(ULT_NAME[skill] ?? ULT_NAME.none);
      this.dom.classList.remove('ult-flash');
      void this.dom.offsetWidth;
      this.dom.classList.add('ult-flash');
      this.numbers.show(`✨ ${name}!`, (me.view.x + foe.view.x) / 2, me.view.y - this.anchor(me.anchor).h - 40, i === 0 ? 0x8e5bd6 : 0xc0392b, 52);
      this.app.sfx.play('crit');
      void me.view.cheer();
      await this.app.tweens.wait(motion.reduced ? 150 : 420);
      if (this.isDestroyed) return healed;
      const dmg = r.ult[i];
      if (dmg > 0) {
        const hitY = foe.view.y - this.anchor(foe.anchor).h * 0.5;
        void foe.view.hit(true);
        void this.screenShake(18);
        this.particles.burst([...this.app.assets.get('fx:star'), ...this.app.assets.get('fx:paper_scrap')], foe.view.x, hitY, 22, { scale: 0.6 });
        this.numbers.show(`-${dmg}`, foe.view.x, hitY - 50, 0x8e5bd6, 68);
        this.setHp(i === 0 ? 1 : 0, Math.max(0, foe.hp - dmg));
      }
      const heal = u.drain ? dmg : u.healPercent ? Math.max(1, Math.floor((me.maxHp * u.healPercent) / 100)) : 0;
      const gained = me.hp > 0 ? Math.min(me.maxHp - me.hp, heal) : 0;
      if (gained > 0) {
        healed[i] = gained;
        this.numbers.show(`+${gained}`, me.view.x, me.view.y - 140, 0x6ab04c, 44);
        this.particles.burst(this.app.assets.get('fx:heart'), me.view.x, me.view.y - 90, 6, { gravity: -60, speed: 90, scale: 0.5 });
        this.app.sfx.play('heal');
        this.setHp(i, me.hp + gained);
      }
      const bits = [dmg > 0 ? t('{n} dmg', { n: dmg }) : '', gained > 0 ? t('+{n} HP', { n: gained }) : '', u.shield ? t('shield up') : ''].filter(Boolean);
      this.log(t('✨ {name} unleashes {ult}! ({effects})', { name: names[i]!, ult: name, effects: bits.join(', ') }), `ultimate ${i === 0 ? 'win' : 'loss'}`);
      await this.app.tweens.wait(motion.reduced ? 150 : 380);
      if (this.isDestroyed) return healed;
    }
    return healed;
  }

  /** The monster's tell (or bluff), a boss charge, an angry boss and the page event, before the hands are picked. */
  private renderPreview(): void {
    if (!(this.driver instanceof PveDriver)) return;
    const p = this.driver.preview;
    const fresh = p !== this.shownPreview;
    this.shownPreview = p;
    const name = this.nameOf(1);
    const foe = this.sides[1];
    foe.card.classList.toggle('raging', p.rage);
    this.dom.classList.toggle('boss-rage', p.rage);
    if (fresh && p.rageStarted) {
      this.numbers.show(`😡 ${t('ANGRY!')}`, foe.view.x, foe.view.y - this.anchor(foe.anchor).h - 30, 0xc0392b, 54);
      void this.screenShake(10);
      this.app.sfx.play('crit');
      this.log(t('😡 {name} is angry! It hits harder, bluffs more and charges more often.', { name }), 'loss');
    }
    const parts: HTMLElement[] = [];
    const tell = p.tell;
    if (tell?.kind === 'move') {
      parts.push(
        h(
          'p',
          { class: 'bt-tell' },
          h('span', { class: 'tell-hand', 'aria-hidden': 'true' }, MOVE_EMOJI[tell.move]),
          t('{name} looks like it is winding up {move}…', { name, move: t(MOVE_LABEL[tell.move]) }),
          this.monster && aiLevelOf(this.monster) > 0 ? h('small', null, t('Clever monsters sometimes bluff!')) : null,
        ),
      );
    } else if (tell?.kind === 'charge') {
      parts.push(h('p', { class: 'bt-tell charge' }, h('span', { class: 'tell-hand', 'aria-hidden': 'true' }, '⚠️'), t('{name} is charging a big hit! It wins: it hits hard. You win: it takes extra damage.', { name })));
    } else if (tell?.kind === 'ultimate') {
      parts.push(h('p', { class: 'bt-tell ultimate' }, h('span', { class: 'tell-hand', 'aria-hidden': 'true' }, '✨'), t("{name}'s ink is full: its ultimate is coming this round!", { name })));
    }
    if (p.event) {
      const [emoji, title, desc] = EVENT_INFO[p.event];
      const what = t(desc, { n: EVENT_NUMBER[p.event] });
      parts.push(h('p', { class: `bt-event event-${p.event}` }, h('b', null, `${emoji} ${t('Page event')}: ${t(title)}`), ' ', what));
      if (fresh) {
        this.log(`${emoji} ${t('Page event')}: ${t(title)} — ${what}`, 'event');
        this.app.sfx.play('flip');
      }
    }
    this.previewEl.classList.remove('resolving');
    setChildren(this.previewEl, parts);
    this.placePreview();
    // Mixed-up Hands: the buttons swap places for this round only.
    if (fresh) {
      const bar = this.buttons[0]?.parentElement;
      const order = p.event === 'shuffle' ? shuffleOrder(this.buttons) : this.buttons;
      bar?.append(...order);
      bar?.classList.toggle('shuffled', p.event === 'shuffle');
    }
  }

  /** Keeps the floating tell at the top of the battle stage, centred over the fighters. */
  private placePreview(): void {
    const stage = this.dom.querySelector('.bt-stage');
    if (!stage || !this.previewEl.isConnected) return;
    const r = stage.getBoundingClientRect();
    this.previewEl.style.left = `${r.left + r.width / 2}px`;
    this.previewEl.style.top = `${r.top + 4}px`;
    this.previewEl.style.width = `${Math.min(r.width * 0.94, 560)}px`;
  }

  /** A page event settled after the round: heals, extra hits, ink. */
  private async playTwist(r: LocalRound): Promise<void> {
    const tw = r.twist!;
    const [emoji, title] = EVENT_INFO[tw.event];
    const label = `${emoji} ${t(title)}`;
    let shown = false;
    for (const i of [0, 1] as const) {
      const side = this.sides[i];
      const y = side.view.y - this.anchor(side.anchor).h * 0.6;
      if (tw.heal[i] > 0) {
        this.numbers.show(`+${tw.heal[i]}`, side.view.x, y, 0xf3a712, 46);
        this.particles.burst(this.app.assets.get('fx:star'), side.view.x, y, 8, { gravity: -60, speed: 90, scale: 0.45 });
        this.app.sfx.play('heal');
        this.log(t('{event}: {name} heals {hp} HP.', { event: label, name: this.nameOf(i), hp: tw.heal[i] }), 'heal');
        shown = true;
      }
      if (tw.extra[i] > 0) {
        this.numbers.show(`-${tw.extra[i]}`, side.view.x, y, 0xe4572e, 46);
        void side.view.hit(false);
        this.app.sfx.play('hit');
        this.log(t('{event}: {name} takes {n} more damage.', { event: label, name: this.nameOf(i), n: tw.extra[i] }), i === 0 ? 'loss' : 'win');
        shown = true;
      }
      this.setHp(i, tw.hp[i]);
    }
    if (tw.ink[0] > 0 || tw.ink[1] > 0) {
      this.log(t('{event}: both sides soak up more ink.', { event: label }), 'event');
      shown = true;
    }
    if (shown) await this.app.tweens.wait(motion.reduced ? 150 : 450);
  }

  /** Combo chain burst: three different winning hands in a row. */
  private async playBurst(r: LocalRound, names: string[]): Promise<void> {
    const w = r.burst!;
    const l = w === 0 ? 1 : 0;
    const foe = this.sides[l];
    const hitY = foe.view.y - this.anchor(foe.anchor).h * 0.5;
    this.numbers.show(`✊✋✌️ ${t('CHAIN!')}`, foe.view.x, hitY - 120, 0xf3a712, 46);
    this.numbers.show(`-${r.burstDamage}`, foe.view.x, hitY - 40, 0xf3a712, 56);
    void foe.view.hit(true);
    void this.screenShake(12);
    this.app.sfx.play('crit');
    this.particles.burst(this.app.assets.get('fx:star'), foe.view.x, hitY, 14, { scale: 0.55 });
    this.setHp(l, r.hp[l]);
    this.log(t('✊✋✌️ Chain burst! {name} won with three different hands: +{n} dmg and extra ink.', { name: names[w]!, n: r.burstDamage }), w === 0 ? 'combo' : 'loss');
    await this.app.tweens.wait(motion.reduced ? 150 : 420);
  }

  private async playEnd(e: BattleEnd): Promise<void> {
    this.setPhase('over');
    this.setStatus(e.outcome === 'draw' ? t('The battle is over — a draw!') : t('The battle is over!'));
    this.buttons.forEach((b) => (b.disabled = true));
    const [me, them] = this.sides;
    if (e.outcome === 'me') {
      void them.view.faint();
      await me.view.cheer();
      if (this.isDestroyed) return;
      this.app.sfx.play('win');
      this.particles.burst(this.app.assets.get('fx:star'), me.view.x, me.view.y - 200, 16, { scale: 0.6 });
    } else if (e.outcome === 'them') {
      void me.view.faint();
      await them.view.cheer();
      if (this.isDestroyed) return;
      this.app.sfx.play('lose');
    } else {
      await this.app.tweens.wait(300);
      if (this.isDestroyed) return;
    }
    this.log(e.outcome === 'draw' ? t('The battle ends in a draw.') : t('{name} wins! ({reason})', { name: this.nameOf(e.outcome === 'me' ? 0 : 1), reason: t(REASON_LABEL[e.reason]) }), 'end');

    let rewards: RewardSummary | null = null;
    let fanPanel: HTMLElement | null = null;
    if (this.params.mode === 'pve' && this.hero && this.monster) {
      const heroId = this.hero.id;
      const monster = this.monster;
      const mine = this.sides[0];
      const hpLeftPercent = Math.max(0, Math.min(100, Math.floor((mine.hp * 100) / Math.max(1, mine.maxHp))));
      this.app.store.update(['hero', 'inventory', 'quests', 'memories', 'monsters'], (s) => {
        const hero = s.heroes.find((x) => x.id === heroId);
        if (!hero) return;
        rewards = applyBattle(s, hero, {
          monster,
          result: e.outcome === 'me' ? 'win' : e.outcome === 'them' ? 'loss' : 'draw',
          rounds: this.driver.round - 1,
          roundWinsByMove: this.stats.roundWinsByMove,
          bestCombo: this.stats.bestCombo,
          hpLeftPercent,
          diceRolls: this.driver instanceof PveDriver ? this.driver.diceRolled : 0,
        });
      });
      const foe = this.sides[1];
      fanPanel = reportCommunityFight(this.app, monster, e.outcome, damageDealt(foe.maxHp, foe.hp));
    } else if (this.params.mode === 'pvp') {
      this.recordPvp(e);
    }
    // Every finished battle helps the class goal (quiet outside a class).
    void reportClassBattle(this.app);
    await this.app.tweens.wait(500);
    if (this.isDestroyed) return;
    this.showResult(e, rewards, fanPanel);
  }

  private recordPvp(e: BattleEnd): void {
    const b = this.app.room.battle;
    const opp = b?.players[this.app.room.mySeat === 0 ? 1 : 0];
    this.app.store.update(['friends', 'quests', 'memories'], (s) => {
      s.counters.pvpBattles++;
      if (opp) {
        let f = s.friends.find((x) => x.id === opp.id);
        if (!f) {
          f = { id: opp.id, name: opp.name, lastSeen: Date.now(), battles: 0, wins: 0, losses: 0 };
          s.friends.unshift(f);
        }
        f.name = opp.name;
        f.lastSeen = Date.now();
        f.battles++;
        if (e.outcome === 'me') f.wins++;
        else if (e.outcome === 'them') f.losses++;
      }
      const name = opp?.name ?? t('a friend');
      addMemory(s, {
        kind: 'friend',
        title: e.outcome === 'me' ? t('Beat {name} online', { name }) : e.outcome === 'them' ? t('Lost to {name} online', { name }) : t('Tied with {name} online', { name }),
        text: `${e.ranked ? t('Ranked: {trophies} trophies.', { trophies: signed(e.trophies) }) : t('Classroom battle (unranked).')} ${t('Ended by {reason}.', { reason: t(REASON_LABEL[e.reason]) })}`,
        sticker: e.outcome === 'me' ? 'crown' : 'pencil',
      });
    });
  }

  /** The stamp bought in the stationery shop, pressed next to VICTORY!. */
  private victoryStamp(): HTMLElement | null {
    const id = this.app.store.state.cosmetics.stamp;
    const def = id ? cosmeticDef(id) : undefined;
    return def ? h('div', { class: 'victory-stamp', 'aria-label': t(def.name) }, stampFace(this.app, def, 'victory-stamp-face')) : null;
  }

  /** One bonus roll after a win: +0-100% of the battle's EXP and coins. */
  private victoryRoll(r: RewardSummary): HTMLElement {
    const box = h('div', { class: 'victory-roll' });
    const roll = button(h('span', { class: 'btn-with-icon' }, '🎲 ', t('Bonus roll!')), {
      color: 'green',
      sfx: this.app,
      onClick: () => {
        roll.disabled = true;
        const heroId = this.hero?.id;
        const face = rollDie();
        let gained = { exp: 0, coins: 0, percent: 0, levelsGained: 0 };
        this.app.store.update(['hero', 'inventory', 'quests'], (s) => {
          const hero = s.heroes.find((x) => x.id === heroId);
          if (hero) gained = applyVictoryRoll(s, hero, r, face);
        });
        this.app.sfx.play(face === 6 ? 'crit' : gained.percent > 0 ? 'pop' : 'miss');
        box.replaceChildren(
          h(
            'p',
            { class: `victory-roll-result face-${face}` },
            h('span', { class: 'dice-face big', 'aria-hidden': 'true' }, ['⚀', '⚁', '⚂', '⚃', '⚄', '⚅'][face - 1]!),
            gained.percent > 0 ? t('Rolled {face}: +{percent}%! +{exp} EXP, +{coins} coins', { face, percent: gained.percent, exp: gained.exp, coins: gained.coins }) : t('Rolled 1: no bonus this time.'),
          ),
        );
        if (gained.levelsGained > 0) box.append(h('p', { class: 'level-up sticky-note green' }, t('LEVEL UP! Now level {level} — spend your new stat points!', { level: this.app.store.hero?.level ?? 0 })));
      },
    });
    box.append(roll, h('small', { class: 'hand-hint' }, t('Roll for up to double rewards.')));
    return box;
  }

  /** "Next floor (8)" for the tower, "Next: Gum Blob ▶" for anything else. */
  private nextLabel(monsterId: string): string {
    const floor = towerFloorOf(monsterId);
    if (floor !== null) return t('Next floor ({n})', { n: floor });
    const m = findMonster(this.app.store.state.monsters, monsterId);
    return m ? t('Next: {monster} ▶', { monster: hasBuiltInName(m) ? t(m.name) : m.name }) : t('Next monster ▶');
  }

  private showResult(e: BattleEnd, rewards: RewardSummary | null, fanPanel: HTMLElement | null = null): void {
    const app = this.app;
    const title = e.outcome === 'me' ? t('VICTORY!') : e.outcome === 'them' ? t('DEFEAT…') : t('DRAW');
    const lines: (HTMLElement | null)[] = [];
    if (rewards) {
      const r: RewardSummary = rewards;
      if (r.practice) lines.push(h('p', null, t('Practice battle (classroom monster): no rewards, no progress.')));
      else {
        if (r.stars > 0) {
          lines.push(
            h(
              'p',
              { class: 'result-stars', 'aria-label': t('{n} of 3 stars', { n: r.stars }) },
              h('span', { class: 'stars' }, starText(r.stars)),
              r.newBestStars ? h('span', { class: 'new-best' }, t('New best!')) : null,
            ),
          );
        }
        lines.push(
          h('p', { class: 'reward-big' }, `+${r.exp} EXP`),
          r.coinsLost > 0
            ? h('p', { class: 'reward-big coins lost' }, h('span', { class: 'coin', 'aria-hidden': 'true' }), ` ${t('-{n} coins (lost {pct}%)', { n: r.coinsLost, pct: LOSS_COIN_PERCENT })}`)
            : h('p', { class: 'reward-big coins' }, h('span', { class: 'coin', 'aria-hidden': 'true' }), ` ${t('+{n} coins', { n: r.coins })}`),
        );
        if (e.outcome === 'me' && (r.exp > 0 || r.coins > 0)) lines.push(this.victoryRoll(r));
        if (r.levelsGained > 0) lines.push(h('p', { class: 'level-up sticky-note green' }, t('LEVEL UP! Now level {level} — spend your new stat points!', { level: r.newLevel })));
        if (r.item) {
          const def = itemDef(r.item);
          lines.push(h('p', { class: 'item-found sticky-note pink' }, artImg(app, `item:${r.item}`, 'item-icon big'), t('Found: {item}!', { item: def ? t(def.name) : r.item })));
        }
        for (const q of r.questsReady) lines.push(h('p', { class: 'quest-ready' }, t('Quest ready to claim: {quest}', { quest: t(q.title) })));
        for (const q of r.dailyReady) lines.push(h('p', { class: 'quest-ready' }, t('Daily quest done: {quest}', { quest: t(q.title) })));
        if (r.injured) lines.push(h('p', { class: 'injury-note sticky-note pink' }, '🤕 ', t('{name} lost {n} times in a row and is hurt: resting for {m} min. Play another hero meanwhile!', { name: this.hero?.name ?? '', n: INJURY_LOSSES, m: INJURY_MINUTES })));
        else if (r.lossStreak > 0) lines.push(h('p', { class: 'injury-warning hand-hint' }, t('Losses in a row: {n}/{max}. At {max} the hero gets hurt and must rest.', { n: r.lossStreak, max: INJURY_LOSSES })));
      }
    } else if (this.params.mode === 'pvp') {
      lines.push(h('p', null, e.ranked ? t('Ranked battle: {trophies} trophies', { trophies: signed(e.trophies) }) : t('Classroom battle — not ranked.')));
      lines.push(h('p', { class: 'hand-hint' }, t('Ended by: {reason}. Results were decided by the server.', { reason: t(REASON_LABEL[e.reason]) })));
    }
    const actions = h('div', { class: 'modal-actions' });
    if (this.params.mode === 'pve') {
      const monsterId = this.params.monsterId;
      actions.append(
        button(t('Fight again'), { color: 'pink', sfx: app, onClick: () => void app.scenes.go('battle', { mode: 'pve', monsterId }, 'fade') }),
      );
      // After a win the next fight is one tap away: no trip back to the notebook.
      const next = e.outcome === 'me' ? nextFight(app.store.state, monsterId) : null;
      if (next) actions.append(button(this.nextLabel(next), { variant: 'primary', color: 'green', sfx: app, onClick: () => void app.scenes.go('battle', { mode: 'pve', monsterId: next }, 'fade') }));
      if (rewards && (rewards as RewardSummary).levelsGained > 0) actions.append(button(t('Upgrade hero'), { color: 'green', sfx: app, onClick: () => void app.scenes.go('notebook', { tab: 'hero' }) }));
      actions.append(
        button(t('Back to notebook'), { variant: next ? undefined : 'primary', color: 'yellow', sfx: app, onClick: () => void app.scenes.go('notebook', { tab: 'quests' }, 'page-back') }),
      );
    } else {
      actions.append(button(t('Back to the room'), { variant: 'primary', color: 'yellow', sfx: app, onClick: () => void app.scenes.go('multiplayer', undefined, 'page-back') }));
    }
    const card = h(
      'div',
      { class: `result-card paper-card ${e.outcome}`, role: 'dialog', 'aria-label': title },
      h('div', { class: 'result-stamp' }, title),
      e.outcome === 'me' ? this.victoryStamp() : null,
      h('div', { class: 'result-body' }, lines, fanPanel),
      actions,
    );
    // Lives in the overlay layer so it sits above the Pixi canvas.
    const wrap = h('div', { class: 'result-wrap' }, card);
    document.getElementById('overlay-layer')?.appendChild(wrap);
    this.d.add(() => wrap.remove());
    actions.querySelector<HTMLButtonElement>('.sketch-btn.primary')?.focus();
  }

  private async runAway(): Promise<void> {
    if (this.phase === 'over') {
      void this.app.scenes.go(this.params.mode === 'pvp' ? 'multiplayer' : 'notebook', undefined, 'page-back');
      return;
    }
    const ok = await confirmBox(
      t('Run away?'),
      this.params.mode === 'pvp' ? t('Running away counts as a loss for this battle.') : t('You will lose this battle (but keep a little EXP).'),
      t('Run!'),
      t('Keep fighting'),
    );
    if (ok) this.driver.forfeit();
  }

  override canLeave(): boolean {
    return true;
  }

  override destroy(): void {
    setChildren(this.logList);
    this.root.mask = null;
    // Left before the end (Back, another screen): the battle still counts as lost.
    // A battle already won or drawn before leaving is simply let go (no penalty, no reward).
    if (this.app.store.state.battleInProgress && this.ended && this.ended.outcome !== 'them') this.app.store.update([], (s) => (s.battleInProgress = null));
    if (this.params?.mode === 'pve' && this.app.store.state.battleInProgress) {
      let fled: ReturnType<typeof forfeitUnfinished> = null;
      this.app.store.update(['hero', 'inventory', 'memories'], (s) => (fled = forfeitUnfinished(s)));
      const f = fled as ReturnType<typeof forfeitUnfinished>;
      if (f) this.app.toast(`${t('You left the battle against {monster} half-way: it counts as a loss.', { monster: f.monster })}${f.injured ? ` ${t('{name} is hurt and must rest for {n} min.', { name: f.hero, n: INJURY_MINUTES })}` : ''}`, 'error');
    }
    super.destroy();
  }
}

/** The items in a new order (never the order they came in). */
function shuffleOrder<T>(items: readonly T[]): T[] {
  const out = [...items];
  do {
    for (let i = out.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [out[i], out[j]] = [out[j]!, out[i]!];
    }
  } while (out.length > 1 && out.every((x, i) => x === items[i]));
  return out;
}
